'use client';

import { Loader2, ChevronLeft, ChevronRight, BookOpen, Shield, Music2, X, Cross, Volume2, VolumeX, Eye, EyeOff, Footprints } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { DiaSS, JartinetHermandad, Banda, DiaSSHermandadDetalles, ProgramaSSConfig } from '@/lib/types';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '@/lib/utils';
import {
  escalaDelMapa, esMadrugada, formatKm, horaDeSalida, longitudEnMetros,
  metrosDelRecorrido, ordenarColumnasCuadro, ordenarHoras, ordenarHorasDesde, type EscalaMapa,
} from '@/lib/programa-ss';
import { RouteMapEditor, CARRERA_OFICIAL_COLOR, darkenColor } from '@/components/programa-ss/RouteMapEditor';

/** El aviso que acompaña siempre al programa, dentro y fuera de Jartinet. */
export const AVISO_PROGRAMA_SS =
  'La información de horarios e itinerarios es provisional y puede estar sujeta a cambios.';

// ─── Sonido de pasar página ──────────────────────────────────────────────────
// Generado con la Web Audio API (ruido blanco filtrado con un barrido de
// frecuencia), igual que los sonidos de JartiBank: sin ficheros de audio que
// descargar y sin dependencias.

let audioCtx: AudioContext | null = null;

function playPageTurn() {
  try {
    const Ctor = window.AudioContext || (window as any).webkitAudioContext;
    if (!Ctor) return;
    audioCtx = audioCtx || new Ctor();
    const ctx = audioCtx;
    // El navegador suspende el contexto hasta que hay interacción del usuario;
    // aquí siempre venimos de un clic, así que basta con reanudarlo.
    if (ctx.state === 'suspended') void ctx.resume();

    const dur = 0.34;
    const buffer = ctx.createBuffer(1, Math.floor(ctx.sampleRate * dur), ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < data.length; i++) data[i] = Math.random() * 2 - 1;

    const src = ctx.createBufferSource();
    src.buffer = buffer;

    // El barrido de agudo a grave es lo que da la sensación de hoja pasando.
    const filtro = ctx.createBiquadFilter();
    filtro.type = 'bandpass';
    filtro.Q.value = 0.7;
    const t = ctx.currentTime;
    filtro.frequency.setValueAtTime(800, t);
    filtro.frequency.exponentialRampToValueAtTime(3200, t + dur * 0.45);
    filtro.frequency.exponentialRampToValueAtTime(700, t + dur);

    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.0001, t);
    gain.gain.exponentialRampToValueAtTime(0.2, t + 0.05);
    gain.gain.exponentialRampToValueAtTime(0.0001, t + dur);

    src.connect(filtro);
    filtro.connect(gain);
    gain.connect(ctx.destination);
    src.start(t);
    src.stop(t + dur);
  } catch {
    // El sonido es un adorno: si el navegador no deja, no pasa nada.
  }
}

function normalizeDetalles(hermandades: DiaSS['hermandades']): DiaSSHermandadDetalles[] {
  if (!Array.isArray(hermandades)) return [];
  return hermandades
    .map((h) => (typeof h === 'string' ? ({ hermandadId: h } as DiaSSHermandadDetalles) : h))
    .filter((h) => h && h.hermandadId);
}

function findTime(detalle: DiaSSHermandadDetalles, tipo: string): string | null {
  const entry = Object.entries(detalle.itinerario || {}).find(([, v]) => v.tipo === tipo);
  return entry ? entry[0] : null;
}

// Exportados para el renderizador de carillas a imagen (`/render/programa-ss`),
// que pinta EXACTAMENTE estas mismas carillas en tamaño fijo para que Chrome las
// fotografíe. Es lo que garantiza que el librito del mod se vea igual que aquí:
// no hay una segunda maquetación que mantener, es esta.
export type BookPage =
  | { type: 'day-cover'; dia: DiaSS; hasHermandades: boolean }
  | { type: 'hermandades'; dia: DiaSS; detalles: DiaSSHermandadDetalles[] }
  | { type: 'itinerarios'; dia: DiaSS; detalles: DiaSSHermandadDetalles[]; ajuste?: AjusteTabla }
  | { type: 'recorridos'; dia: DiaSS; detalles: DiaSSHermandadDetalles[] };

/** Eje de horas del día (el que comparten todas las hermandades). */
export function horasDelDia(detalles: DiaSSHermandadDetalles[]): string[] {
  const slots = new Set<string>();
  detalles.forEach((h) => Object.keys(h.itinerario || {}).forEach((t) => slots.add(t)));
  return ordenarHoras(Array.from(slots));
}

/** Ajuste con el que se dibuja el cuadro de horarios para que quepa entero. */
export interface AjusteTabla {
  /** Padding vertical de cada celda, en px. */
  padY: number;
  /** Tamaño de letra, en px. */
  fuente: number;
}

/**
 * Ancho máximo del hueco de un nazareno, en proporción a su alto.
 *
 * Las imágenes que se suben son **cuadradas** (512×512) con la figura dibujada
 * pequeña en el centro y un montón de transparente a los lados: respetar esa
 * caja se comía media tarjeta de aire y apartaba el texto. Así que la caja se
 * estrecha a esta proporción y la imagen se recorta con `object-cover`; lo que
 * se pierde por los lados es transparencia. La túnica ocupa ~0,34 del cuadrado,
 * o sea que 0,44 la deja entera con holgura.
 *
 * Si la imagen ya viene recortada (más estrecha que esto), manda la suya y no
 * se recorta nada: se usa `Math.min(proporción real, este tope)`.
 */
const RATIO_MAX_NAZARENO = 0.44;

/** Punto de partida: lo que se ve en la web (p-1.5 = 6 px, texto 0.65rem). */
const TABLA_HOLGADA: AjusteTabla = { padY: 6, fuente: 10.4 };

/**
 * Busca con qué apretura cabe el cuadro de horarios ENTERO en una carilla.
 *
 * Solo se usa al renderizar el libro a imagen (el librito del mod): en la web la
 * tabla scrollea dentro de su tarjeta y no se pierde nada, pero **en una
 * fotografía lo que sobra por abajo desaparece**.
 *
 * El orden lo pidió el usuario y es el correcto: **primero se aprieta el alto de
 * la celda** (que es aire) y **solo cuando el padding ya no da más de sí se baja
 * la letra**, que es lo que de verdad se nota al leer. Nunca se parte el cuadro
 * en varias carillas.
 *
 * Las alturas están **MEDIDAS** sobre una carilla ya renderizada, no estimadas a
 * ojo: una fila de una línea son 29 px con el padding de 6, es decir 17 px de
 * texto + 2×6 de padding.
 */
function ajustarTabla(
  detalles: DiaSSHermandadDetalles[],
  horas: string[],
  anchoCarilla: number,
  altoCarilla: number,
): AjusteTabla {
  const PADDING = 48;   // el de la carilla (24 a cada lado)
  const COL_HORA = 64;  // ancho fijo de la columna de horas
  const CABECERA = 120; // cabecera de carilla + rótulo + thead (a 2 líneas)
  const HOLGURA = 24;
  const presupuesto = altoCarilla - PADDING - CABECERA - HOLGURA;
  const columnas = Math.max(1, detalles.length);
  const anchoCol = Math.max(40, (anchoCarilla - PADDING - COL_HORA) / columnas);

  const alturaCon = (a: AjusteTabla) => {
    // A menos letra entran más caracteres por línea, así que también se envuelve
    // menos: hay que recalcularlo con CADA candidato, no una vez.
    const charsPorLinea = Math.max(8, Math.floor(anchoCol / (a.fuente * 0.48)));
    const altoLinea = a.fuente * 1.25;
    let total = 0;
    for (const hora of horas) {
      let lineas = 1;
      for (const d of detalles) {
        const texto = d.itinerario?.[hora]?.calle || '';
        lineas = Math.max(lineas, Math.ceil(texto.length / charsPorLinea) || 1);
      }
      total += lineas * altoLinea + a.padY * 2;
    }
    return total;
  };

  if (alturaCon(TABLA_HOLGADA) <= presupuesto) {
    return TABLA_HOLGADA;
  }

  // 1) Apretar el padding: de 6 px hasta 1, que es donde el texto empieza a
  //    rozar el filete de la celda.
  for (let padY = 5; padY >= 1; padY--) {
    const a = { padY, fuente: TABLA_HOLGADA.fuente };
    if (alturaCon(a) <= presupuesto) return a;
  }

  // 2) Y solo entonces, bajar la letra. Suelo en 6,5 px: por debajo no se lee
  //    aunque quepa, y es preferible que roce a que desaparezca información.
  for (let fuente = 10; fuente >= 6.5; fuente -= 0.25) {
    const a = { padY: 1, fuente };
    if (alturaCon(a) <= presupuesto) return a;
  }
  return { padY: 1, fuente: 6.5 };
}

/**
 * @param carilla tamaño de la carilla en px CSS. Si se pasa, el cuadro de
 *                horarios se APRIETA para caber entero (modo render a imagen).
 *                Sin él, el libro se comporta como siempre: tabla holgada con
 *                scroll dentro de su tarjeta.
 */
export function buildPages(
  days: DiaSS[],
  cityMapUrl: string | null,
  carilla?: { ancho: number; alto: number },
): BookPage[] {
  const pages: BookPage[] = [];
  days.forEach((dia) => {
    const detalles = normalizeDetalles(dia.hermandades);
    pages.push({ type: 'day-cover', dia, hasHermandades: detalles.length > 0 });
    if (detalles.length > 0) {
      for (let i = 0; i < detalles.length; i += 2) {
        pages.push({ type: 'hermandades', dia, detalles: detalles.slice(i, i + 2) });
      }
      const hasItinerary = detalles.some((d) => d.itinerario && Object.keys(d.itinerario).length > 0);
      if (hasItinerary) {
        // El cuadro se lee de IZQUIERDA A DERECHA por orden de paso: la primera
        // columna es la hermandad que antes cruza la Carrera Oficial y, en los
        // días de vísperas (que no la recorren), la que antes sale.
        const columnas = ordenarColumnasCuadro(detalles, dia.tipo === 'visperas');
        pages.push({
          type: 'itinerarios', dia, detalles: columnas,
          ajuste: carilla ? ajustarTabla(columnas, horasDelDia(columnas), carilla.ancho, carilla.alto) : undefined,
        });
      }
      if (cityMapUrl) pages.push({ type: 'recorridos', dia, detalles });
    }
  });
  return pages;
}

interface Spread {
  left: BookPage;
  right: BookPage | null;
}

/**
 * Pliegos del libro. Una hoja física tiene DOS caras, así que al voltearla
 * cambian las dos carillas a la vez: la izquierda pasa a ser el dorso de la
 * hoja que acabas de pasar y la derecha, la cara de la siguiente. Por eso el
 * pliego avanza de dos en dos (1|2 → 3|4 → 5|6) y ninguna carilla se repite.
 * Lo que se anima como una sola hoja es el gesto, no el contenido (`BookLeaf`).
 */
function buildSpreads(pages: BookPage[]): Spread[] {
  const spreads: Spread[] = [];
  for (let i = 0; i < pages.length; i += 2) {
    spreads.push({ left: pages[i], right: pages[i + 1] || null });
  }
  return spreads;
}

/** Fecha del día en largo ("viernes, 28 de agosto de 2026"). */
function formatFechaLarga(fecha: any): string {
  if (!fecha) return '';
  const d = typeof fecha?.toDate === 'function' ? fecha.toDate() : new Date(fecha);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
}

/** Fecha corta para la cabecera de cada carilla ("28 de agosto"). */
function formatFechaCorta(fecha: any): string {
  if (!fecha) return '';
  const d = typeof fecha?.toDate === 'function' ? fecha.toDate() : new Date(fecha);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString('es-ES', { day: 'numeric', month: 'long' });
}

function PageHeader({ dia }: { dia: DiaSS }) {
  const fecha = formatFechaCorta(dia.fecha);
  return (
    <div className="mb-3 flex items-center justify-between gap-2 text-[0.65rem] uppercase tracking-wider text-muted-foreground">
      <span className="flex min-w-0 items-center gap-1.5">
        <Cross className="h-3 w-3 flex-shrink-0 text-primary" />
        <span>{dia.nombredia}</span>
        {fecha && (
          <>
            <span className="text-primary/50">·</span>
            <span className="normal-case text-muted-foreground/80">{fecha}</span>
          </>
        )}
      </span>
      <span className="flex-shrink-0">Semana Santa 2026</span>
    </div>
  );
}

function HermandadHalfCard({ detalle, allHermandades, allBandas, escala, metrosCarreraOficial }: {
  detalle: DiaSSHermandadDetalles;
  allHermandades: JartinetHermandad[];
  allBandas: Banda[];
  escala: EscalaMapa | null;
  /** Metros de Carrera Oficial que suma este día (0 en las vísperas). */
  metrosCarreraOficial: number;
}) {
  const hermandad = allHermandades.find((h) => h.id === detalle.hermandadId);

  // Proporción real de cada imagen de nazareno (ancho ÷ alto), medida al
  // cargarla. Ver `RATIO_MAX_NAZARENO`.
  const [ratios, setRatios] = useState<Record<number, number>>({});

  const itinerarioTexto = useMemo(() => {
    const itinerario = detalle.itinerario || {};
    return ordenarHorasDesde(Object.keys(itinerario), horaDeSalida(itinerario))
      .map((hora) => itinerario[hora].calle)
      .join(', ');
  }, [detalle.itinerario]);

  // El mapa del programa es el del servidor, así que conocemos la coordenada de
  // Minecraft de cada punto: 1 bloque = 1 metro y el recorrido se puede medir.
  const metros = useMemo(
    () => metrosDelRecorrido(detalle.recorrido, escala, metrosCarreraOficial),
    [detalle.recorrido, escala, metrosCarreraOficial],
  );

  if (!hermandad) return null;

  const acompanamiento = detalle.acompanamientoMusical || [];
  // Una hermandad puede tener dos hábitos (p. ej. nazarenos de cada paso).
  const nazarenos = [detalle.nazarenoUrl, detalle.nazarenoUrl2].filter(Boolean) as string[];
  const bandaPaso1 = allBandas.find((b) => b.id === acompanamiento[0]);
  const bandaPaso2 = detalle.pasos === '2 PASOS' ? allBandas.find((b) => b.id === acompanamiento[1]) : null;

  const ribbonClip = { clipPath: 'polygon(1.5% 0%, 98.5% 0%, 100% 50%, 98.5% 100%, 1.5% 100%, 0% 50%)' };
  const salida = findTime(detalle, 'salida');
  const carrera = findTime(detalle, 'carreraOficial');
  const entrada = findTime(detalle, 'entrada');

  return (
    <div className="relative flex h-1/2 flex-col overflow-hidden rounded-lg border border-primary/20 bg-card/40">
      <img
        src={hermandad.escudoUrl}
        alt=""
        className="pointer-events-none absolute -bottom-8 -right-8 h-44 w-44 rotate-[-10deg] object-contain opacity-[0.06]"
      />

      <div className="relative z-10 flex items-center gap-3 bg-primary px-4 py-2 text-primary-foreground" style={ribbonClip}>
        <h3 className="flex-shrink-0 font-headline text-lg font-extrabold uppercase leading-none tracking-tight">{hermandad.nombre}</h3>
        <p className="flex-1 text-[0.6rem] font-medium leading-tight opacity-90">{hermandad.nombreCompleto}</p>
        <img src={hermandad.escudoUrl} alt="" className="h-16 w-16 flex-shrink-0 object-contain drop-shadow-[0_3px_5px_rgba(0,0,0,0.6)]" />
      </div>

      <div className="relative z-10 flex flex-1 gap-3 overflow-hidden p-3">
        {/* Foto de la hermandad: columna estrecha a la izquierda. */}
        {detalle.fotoUrl && (
          <img src={detalle.fotoUrl} alt={hermandad.nombre} className="h-full w-20 flex-shrink-0 rounded-md object-cover" />
        )}

        {/* Ningún dato se recorta: el texto sale entero y, si no cabe, es esta
            columna la que se desplaza (no se pierde información). */}
        <div className="flex-1 space-y-1 overflow-y-auto pr-1 text-[0.68rem] leading-snug text-muted-foreground">
          <div className="grid grid-cols-2 gap-x-2">
            <p><strong className="text-foreground">Fundación:</strong> {hermandad.fundacion}</p>
            <p><strong className="text-foreground">Nazarenos:</strong> {detalle.numeroDeNazarenos}</p>
          </div>
          <p><strong className="text-foreground">Sede:</strong> {hermandad.sede}</p>
          {metros !== null && (
            <p className="flex items-center gap-1">
              <Footprints className="h-3 w-3 flex-shrink-0 text-primary" />
              <strong className="text-foreground">Recorrido:</strong>
              <span className="font-semibold text-primary">{formatKm(metros)}</span>
              {metrosCarreraOficial > 0 && <span className="opacity-60">· Carrera Oficial incluida</span>}
            </p>
          )}
          {hermandad.titulares && <p><strong className="text-foreground">Titulares:</strong> {hermandad.titulares}</p>}
          {detalle.habito && <p><strong className="text-foreground">Hábito nazareno:</strong> {detalle.habito}</p>}

          <div>
            <strong className="flex items-center gap-1 text-foreground">
              <Music2 className="h-3 w-3 flex-shrink-0 text-primary" />
              Acompañamiento musical:
            </strong>
            <div className="mt-0.5 flex flex-col gap-0.5 pl-4">
              {!bandaPaso1 && !bandaPaso2 && <span>Sin banda</span>}
              {[bandaPaso1, bandaPaso2].map((banda, i) =>
                banda ? (
                  <span key={banda.id} className="flex items-start gap-1.5">
                    {banda.escudoUrl && (
                      <img src={banda.escudoUrl} alt="" className="mt-px h-4 w-4 flex-shrink-0 object-contain drop-shadow-[0_1px_2px_rgba(0,0,0,0.6)]" />
                    )}
                    <span>
                      {banda.nombreCompleto}
                      {bandaPaso2 && <span className="opacity-60"> · paso {i + 1}</span>}
                    </span>
                  </span>
                ) : null,
              )}
            </div>
          </div>

          {itinerarioTexto && (
            <p><strong className="text-foreground">Itinerario:</strong> {itinerarioTexto}</p>
          )}
        </div>

        {/* Nazareno(s), pegados al borde derecho y entre sí.
            Cada figura vive en una caja **estrecha** cuyo ancho sale del alto
            (`aspect-ratio`), y la imagen la rellena con `object-cover`: lo que
            se recorta a los lados es el transparente del PNG, nunca la túnica.
            Las imágenes van en posición absoluta dentro de su caja, así que no
            aportan tamaño intrínseco y **no pueden estirar la tarjeta**.
            El `-my-3`/`-mr-3` se comen el padding de la fila: la figura llega
            al borde de la tarjeta y gana los últimos 24 px de alto. */}
        {nazarenos.length > 0 && (
          <div className="-my-3 -mr-3 flex h-[calc(100%+1.5rem)] flex-shrink-0 items-stretch">
            {nazarenos.map((url, i) => (
              <div
                key={i}
                className={cn('relative h-full flex-shrink-0 overflow-hidden', i > 0 && '-ml-5')}
                style={{ aspectRatio: `${Math.min(ratios[i] ?? RATIO_MAX_NAZARENO, RATIO_MAX_NAZARENO)}` }}
              >
                <img
                  src={url}
                  alt={`Hábito nazareno de ${hermandad.nombre}`}
                  onLoad={(e) => {
                    const im = e.currentTarget;
                    if (im.naturalHeight) setRatios((r) => ({ ...r, [i]: im.naturalWidth / im.naturalHeight }));
                  }}
                  className="absolute inset-0 h-full w-full object-cover object-bottom"
                />
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="relative z-10 flex items-center justify-between bg-primary px-4 py-1.5 text-[0.62rem] font-bold text-primary-foreground" style={ribbonClip}>
        <span>{detalle.pasos}</span>
        <span>SALIDA: {salida || '—'}</span>
        <span>CARRERA OFICIAL: {carrera || '—'}</span>
        <span>ENTRADA: {entrada || '—'}</span>
        <span>{detalle.pasos}</span>
      </div>
    </div>
  );
}

function HermandadesPageSheet({ page, allHermandades, allBandas, escala, metrosCarreraOficial }: {
  page: BookPage & { type: 'hermandades' };
  allHermandades: JartinetHermandad[];
  allBandas: Banda[];
  escala: EscalaMapa | null;
  metrosCarreraOficial: number;
}) {
  // Las vísperas no recorren la Carrera Oficial, así que no se les suma.
  const carrera = page.dia.tipo === 'visperas' ? 0 : metrosCarreraOficial;
  return (
    <div className="flex h-full flex-col">
      <PageHeader dia={page.dia} />
      {/* `min-h-0` NO es decorativo: sin él este contenedor es un flex item con
          `min-height: auto`, o sea que **crece hasta el contenido** en vez de
          quedarse en su mitad de carilla. Bastaba con que una imagen fuera algo
          grande para que las dos tarjetas del día se estiraran (`h-1/2` pasa a
          ser la mitad de un alto crecido) y ese día tuviera las tarjetas de otro
          tamaño que el resto. Era justo lo que no puede pasar. */}
      <div className="flex min-h-0 flex-1 flex-col gap-3">
        <HermandadHalfCard detalle={page.detalles[0]} allHermandades={allHermandades} allBandas={allBandas} escala={escala} metrosCarreraOficial={carrera} />
        {page.detalles[1] ? (
          <HermandadHalfCard detalle={page.detalles[1]} allHermandades={allHermandades} allBandas={allBandas} escala={escala} metrosCarreraOficial={carrera} />
        ) : (
          <div className="h-1/2" />
        )}
      </div>
    </div>
  );
}

function ItinerarioPage({ dia, detalles, allHermandades, ajuste }: {
  dia: DiaSS;
  detalles: DiaSSHermandadDetalles[];
  allHermandades: JartinetHermandad[];
  /** Apretura calculada para que quepa entero (solo en el render a imagen). */
  ajuste?: AjusteTabla;
}) {
  // El eje de horas lo comparten todas las hermandades del día, así que aquí
  // manda la regla general: lo que cae antes de las 07:00 es madrugada y se va
  // abajo del todo (ya es el día siguiente).
  const timeSlots = useMemo(() => {
    const slots = new Set<string>();
    detalles.forEach((h) => Object.keys(h.itinerario || {}).forEach((t) => slots.add(t)));
    return ordenarHoras(Array.from(slots));
  }, [detalles]);

  // Los valores del ajuste van en `style`, no en clases: Tailwind no puede
  // generar tamaños dinámicos. Sin ajuste (la web) manda la clase de siempre.
  const estiloCelda: React.CSSProperties | undefined = ajuste
    ? { paddingTop: ajuste.padY, paddingBottom: ajuste.padY, paddingLeft: 4, paddingRight: 4, lineHeight: 1.25 }
    : undefined;
  const estiloTabla: React.CSSProperties = ajuste
    ? { borderSpacing: 0, fontSize: `${ajuste.fuente}px` }
    : { borderSpacing: 0 };

  const primeraMadrugada = timeSlots.find((t) => esMadrugada(t));

  // Carrera Oficial en rojo y Catedral en azul claro: los dos tramos que se
  // buscan de un vistazo en el cuadro.
  const getCellStyles = (tipo?: string) => {
    switch (tipo) {
      case 'salida':
      case 'entrada':
        return 'bg-foreground text-background font-bold';
      case 'carreraOficial':
        return 'bg-red-600/85 text-white font-semibold';
      case 'catedral':
        return 'bg-sky-300/85 text-slate-900 font-semibold';
      default:
        return '';
    }
  };

  return (
    <div className="flex h-full flex-col">
      <PageHeader dia={dia} />
      <div className="flex flex-1 flex-col overflow-hidden rounded-lg border border-primary/20">
        <div className="bg-primary py-2 text-center">
          <h2 className="font-headline text-base font-extrabold uppercase tracking-wide text-primary-foreground">Itinerarios</h2>
        </div>
        <div className="flex-1 overflow-auto">
          <table className={cn('w-full border-separate', !ajuste && 'text-[0.65rem]')} style={estiloTabla}>
            <thead className="sticky top-0 z-10 bg-card">
              <tr>
                <th className="sticky left-0 z-20 w-16 border-b border-primary/20 bg-card p-1.5 text-center uppercase text-muted-foreground">Hora</th>
                {detalles.map((h) => (
                  <th key={h.hermandadId} className="border-b border-l border-primary/20 bg-card p-1.5 text-center uppercase text-muted-foreground">
                    {allHermandades.find((ah) => ah.id === h.hermandadId)?.nombre || '—'}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {timeSlots.map((time) => (
                <tr key={time}>
                  {/* La primera hora de madrugada abre el día siguiente: se marca
                      con un filete para que se vea dónde cambia la fecha. */}
                  <td style={estiloCelda} className={cn(
                    'sticky left-0 z-10 border-b border-primary/10 bg-card text-center font-mono font-semibold',
                    !ajuste && 'p-1.5',
                    time === primeraMadrugada && 'border-t-2 border-t-sky-400/70',
                    esMadrugada(time) && 'text-sky-300',
                  )}>
                    {time}
                    {time === primeraMadrugada && (
                      <span className="block text-[0.5rem] font-bold uppercase tracking-wider text-sky-400">Día siguiente</span>
                    )}
                  </td>
                  {detalles.map((h) => {
                    const entry = h.itinerario?.[time];
                    return (
                      <td key={`${h.hermandadId}-${time}`} style={estiloCelda} className={cn(
                        'border-b border-l border-primary/10 text-center',
                        !ajuste && 'p-1.5',
                        time === primeraMadrugada && 'border-t-2 border-t-sky-400/70',
                        getCellStyles(entry?.tipo),
                      )}>
                        {entry?.calle || ''}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function RecorridosPage({ dia, detalles, allHermandades, cityMapUrl, carreraOficial, catedralPunto, escala, metrosCarreraOficial }: {
  dia: DiaSS;
  detalles: DiaSSHermandadDetalles[];
  allHermandades: JartinetHermandad[];
  cityMapUrl: string;
  carreraOficial: { x: number; y: number }[];
  catedralPunto?: { x: number; y: number };
  escala: EscalaMapa | null;
  metrosCarreraOficial: number;
}) {
  const todos = detalles.filter((d) => d.recorrido?.estado === 'aprobado');
  // Los días de vísperas no tienen Carrera Oficial ni pasan por la Catedral:
  // cada cofradía marca la iglesia donde hace su estación de penitencia.
  const esVispera = dia.tipo === 'visperas';
  const mostrarCarrera = !esVispera && carreraOficial.length >= 2;

  // Con varias cofradías el mapa se hace un ovillo, así que se puede apagar el
  // recorrido de las que estorben. Por defecto salen TODAS.
  const [ocultas, setOcultas] = useState<string[]>([]);
  const aprobados = todos.filter((d) => !ocultas.includes(d.hermandadId));
  const alternar = (id: string) =>
    setOcultas((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  return (
    <div className="flex h-full flex-col">
      <PageHeader dia={dia} />
      <div className="flex flex-1 flex-col overflow-hidden rounded-lg border border-primary/20">
        <div className="bg-primary py-2 text-center">
          <h2 className="font-headline text-base font-extrabold uppercase tracking-wide text-primary-foreground">Recorridos</h2>
        </div>
        {/* `min-h-0`: sin él, al ampliar el mapa este hueco crece con su
            contenido (el zoom hace la imagen más alta) y empuja la leyenda
            fuera de la carilla, que la recorta. Mismo fallo que el de las
            tarjetas de hermandad [147]. */}
        <div className="min-h-0 flex-1 p-2">
          <RouteMapEditor
            mapImageUrl={cityMapUrl}
            reference={mostrarCarrera ? { puntos: carreraOficial, color: CARRERA_OFICIAL_COLOR } : null}
            segments={aprobados.flatMap((d) => [
              { puntos: d.recorrido!.ida, color: d.recorrido!.color || '#3b82f6' },
              { puntos: d.recorrido!.vuelta, color: darkenColor(d.recorrido!.color || '#3b82f6'), dashed: true },
            ])}
            showSegmentPoints={false}
            markers={[
              ...aprobados.map((d) => ({
                point: d.recorrido!.iglesia,
                color: d.recorrido!.color || '#3b82f6',
                iconUrl: allHermandades.find((h) => h.id === d.hermandadId)?.escudoUrl,
              })),
              ...(esVispera
                ? aprobados
                    .filter((d) => d.recorrido!.estacion)
                    .map((d) => ({ point: d.recorrido!.estacion!, color: d.recorrido!.color || '#e11d48', cross: true }))
                : []),
            ]}
            catedral={!esVispera && catedralPunto ? { point: catedralPunto } : null}
            className="h-full"
          />
        </div>
        <div className="flex flex-col gap-1.5 border-t border-primary/10 p-2">
          <div className="flex flex-wrap items-center gap-3">
            {mostrarCarrera && (
              <div className="flex items-center gap-1 text-[0.6rem] text-muted-foreground">
                <span className="h-2 w-3 rounded-full" style={{ backgroundColor: CARRERA_OFICIAL_COLOR }} /> Carrera Oficial
              </div>
            )}
            {esVispera && (
              <div className="flex items-center gap-1 text-[0.6rem] text-muted-foreground">
                <span className="grid h-2.5 w-2.5 place-items-center text-[0.5rem] leading-none">✝</span> Estación de penitencia
              </div>
            )}
            {!esVispera && catedralPunto && (
              <div className="flex items-center gap-1 text-[0.6rem] text-muted-foreground">
                <span className="h-2.5 w-2.5 rounded-full bg-yellow-500" /> Catedral
              </div>
            )}
            {todos.length > 0 && <span className="text-[0.6rem] italic text-muted-foreground">(línea continua = ida · línea discontinua = vuelta)</span>}
            {ocultas.length > 0 && (
              <button
                type="button"
                onClick={() => setOcultas([])}
                className="ml-auto flex items-center gap-1 rounded-full border border-primary/30 bg-primary/10 px-2 py-0.5 text-[0.6rem] font-semibold text-primary transition-colors hover:bg-primary/20"
              >
                <Eye className="h-2.5 w-2.5" /> Ver todas ({ocultas.length} ocultas)
              </button>
            )}
          </div>

          {/* La leyenda es también el interruptor de cada cofradía: clic y su
              recorrido desaparece del mapa. Todas empiezan visibles. */}
          <div className="flex flex-wrap gap-1.5">
            {todos.map((d) => {
              const oculta = ocultas.includes(d.hermandadId);
              const metros = metrosDelRecorrido(d.recorrido, escala, esVispera ? 0 : metrosCarreraOficial);
              return (
                <button
                  key={d.hermandadId}
                  type="button"
                  onClick={() => alternar(d.hermandadId)}
                  title={oculta ? 'Mostrar este recorrido' : 'Ocultar este recorrido'}
                  className={cn(
                    'flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[0.6rem] transition-colors',
                    oculta
                      ? 'border-white/10 text-muted-foreground/50 line-through hover:border-white/25'
                      : 'border-white/15 text-muted-foreground hover:border-white/30 hover:text-foreground',
                  )}
                >
                  <span
                    className="h-2 w-3 flex-shrink-0 rounded-full"
                    style={{ backgroundColor: d.recorrido!.color, opacity: oculta ? 0.25 : 1 }}
                  />
                  {allHermandades.find((h) => h.id === d.hermandadId)?.nombre || '—'}
                  {metros !== null && <span className="font-semibold text-primary/80">{formatKm(metros)}</span>}
                  {oculta ? <EyeOff className="h-2.5 w-2.5" /> : <Eye className="h-2.5 w-2.5 opacity-40" />}
                </button>
              );
            })}
            {todos.length === 0 && <p className="text-[0.6rem] text-muted-foreground">Sin recorridos confirmados todavía.</p>}
          </div>
        </div>
      </div>
    </div>
  );
}

function DayCoverPage({ page }: { page: BookPage & { type: 'day-cover' } }) {
  const fechaLarga = formatFechaLarga(page.dia.fecha);
  return (
    <div className="relative flex h-full flex-col items-center justify-center overflow-hidden rounded-lg border border-primary/20 text-center">
      {page.dia.portadaUrl ? (
        <>
          <img src={page.dia.portadaUrl} alt={page.dia.nombredia} className="absolute inset-0 h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-background via-background/50 to-background/20" />
        </>
      ) : (
        <div className="absolute inset-0 bg-gradient-to-br from-primary/15 via-background to-background" />
      )}
      <div className="relative z-10 px-4">
        <span className="text-xs font-bold uppercase tracking-[0.3em] text-primary">{page.dia.tipo === 'visperas' ? 'Vísperas' : 'Semana Santa 2026'}</span>
        <h2 className="mt-3 font-headline text-4xl font-extrabold uppercase drop-shadow-lg">{page.dia.nombredia}</h2>
        {fechaLarga && (
          <div className="mt-3 flex items-center justify-center gap-3">
            <span className="h-px w-8 bg-primary/50" />
            <p className="text-base font-semibold capitalize text-primary/90 drop-shadow">{fechaLarga}</p>
            <span className="h-px w-8 bg-primary/50" />
          </div>
        )}
        {!page.hasHermandades && <p className="mt-3 text-sm text-muted-foreground">Día sin procesiones programadas.</p>}
      </div>
    </div>
  );
}

/**
 * Una carilla del libro con su animación de pasar hoja.
 *
 * Al avanzar, la hoja física es la carilla DERECHA: se levanta y gira hacia el
 * lomo (su borde izquierdo), y por detrás aparece la nueva carilla izquierda
 * girando desde +90° sobre su borde derecho. La carilla que queda debajo
 * simplemente se descubre (fundido), sin girar. Al retroceder, al revés.
 */
function BookLeaf({
  side,
  pageKey,
  direction,
  children,
}: {
  side: 'left' | 'right';
  pageKey: string;
  direction: number;
  children: React.ReactNode;
}) {
  const adelante = direction > 0;
  // La hoja gira siempre sobre el lomo: el borde interior de cada carilla.
  const origin = side === 'left' ? 'right center' : 'left center';
  // Solo gira la carilla que hace de hoja; la otra se descubre con un fundido.
  const giraAlEntrar = side === (adelante ? 'left' : 'right');
  const giraAlSalir = side === (adelante ? 'right' : 'left');
  const anguloEntrada = side === 'left' ? 90 : -90;
  const anguloSalida = side === 'right' ? -90 : 90;

  return (
    <div className="h-full" style={{ perspective: 2000 }}>
      <AnimatePresence mode="wait" custom={direction}>
        <motion.div
          key={pageKey}
          custom={direction}
          initial={{ rotateY: giraAlEntrar ? anguloEntrada : 0, opacity: giraAlEntrar ? 1 : 0 }}
          animate={{ rotateY: 0, opacity: 1 }}
          exit={{ rotateY: giraAlSalir ? anguloSalida : 0, opacity: giraAlSalir ? 1 : 0 }}
          // Con `mode="wait"` la salida y la entrada van en dos tiempos: la hoja
          // se despega de un lado y después aterriza en el otro, que es justo el
          // gesto de pasar página. Por eso la duración es corta.
          transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
          style={{ transformOrigin: origin, transformStyle: 'preserve-3d', backfaceVisibility: 'hidden' }}
          className="h-full"
        >
          {children}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}

/**
 * Tamaño de UNA carilla, en px CSS. Es fijo a propósito y es el mismo dentro y
 * fuera del juego: el render del librito [144] dibuja exactamente esta caja.
 */
export const CARILLA_ANCHO = 600;
export const CARILLA_ALTO = 800;

/**
 * La carilla se dibuja SIEMPRE a 600×800 px y se escala entera para caber en el
 * hueco que haya. Es la única forma de que **la maqueta no cambie nunca**: antes
 * la carilla era `min-h-[80vh]` con el ancho fluido, así que al mover el zoom o
 * estrechar la ventana cambiaba su proporción y con ella el alto de las tarjetas
 * de hermandad (y en pantallas estrechas se solapaba todo).
 *
 * Al escalar con `transform` no se recalcula nada del interior: lo que cambia es
 * el tamaño en pantalla, no la maqueta. Por eso el ancho de la carilla es una
 * constante y **dentro de ella no se usan breakpoints** (`md:`), que miran el
 * viewport y no la caja.
 *
 * El alto sale de la proporción (`aspect-ratio`), así que no hay pescadilla:
 * primero se sabe el ancho disponible y de ahí el alto y la escala.
 */
export function CarillaFija({ children }: { children: React.ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  const [escala, setEscala] = useState(0);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const medir = () => setEscala(el.clientWidth / CARILLA_ANCHO);
    medir();
    // El zoom del navegador y el redimensionado de la ventana llegan los dos
    // por aquí; no hace falta escuchar `resize` aparte.
    const ro = new ResizeObserver(medir);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <div ref={ref} className="relative w-full" style={{ aspectRatio: `${CARILLA_ANCHO} / ${CARILLA_ALTO}` }}>
      <div
        className="absolute left-0 top-0 origin-top-left overflow-hidden"
        style={{
          width: CARILLA_ANCHO,
          height: CARILLA_ALTO,
          // El mismo margen interior que pone la ruta de render: así el hueco
          // útil es idéntico (552×752) y la web enseña exactamente lo que sale
          // fotografiado en el librito.
          padding: 24,
          transform: `scale(${escala})`,
          // Hasta la primera medida no se pinta, para no enseñar la carilla a
          // tamaño natural desbordando el hueco.
          visibility: escala ? 'visible' : 'hidden',
        }}
      >
        {children}
      </div>
    </div>
  );
}

export function HalfPageSlot({
  page,
  allHermandades,
  allBandas,
  cityMapUrl,
  carreraOficial,
  catedralPunto,
  escala,
  metrosCarreraOficial,
}: {
  page: BookPage | null;
  allHermandades: JartinetHermandad[];
  allBandas: Banda[];
  cityMapUrl: string | null;
  carreraOficial: { x: number; y: number }[];
  catedralPunto?: { x: number; y: number };
  escala: EscalaMapa | null;
  metrosCarreraOficial: number;
}) {
  if (!page) {
    return <div className="h-full rounded-lg border border-dashed border-primary/10" />;
  }
  if (page.type === 'day-cover') return <DayCoverPage page={page} />;
  if (page.type === 'hermandades') {
    return <HermandadesPageSheet page={page} allHermandades={allHermandades} allBandas={allBandas} escala={escala} metrosCarreraOficial={metrosCarreraOficial} />;
  }
  if (page.type === 'itinerarios') {
    return <ItinerarioPage dia={page.dia} detalles={page.detalles} allHermandades={allHermandades} ajuste={page.ajuste} />;
  }
  if (page.type === 'recorridos' && cityMapUrl) {
    return (
      <RecorridosPage
        dia={page.dia}
        detalles={page.detalles}
        allHermandades={allHermandades}
        cityMapUrl={cityMapUrl}
        carreraOficial={carreraOficial}
        catedralPunto={catedralPunto}
        escala={escala}
        metrosCarreraOficial={metrosCarreraOficial}
      />
    );
  }
  return null;
}

/**
 * El libro del Programa de Semana Santa. No carga nada: recibe ya los datos,
 * porque lo usan dos sitios con orígenes distintos — la página de Jartinet (que
 * saca hermandades y bandas de `jartinetData`, ya en memoria) y la portada
 * pública (que las lee de Firestore, colecciones de lectura abierta).
 */
export function ProgramaSSBook({
  days,
  config,
  cityMapUrl,
  allHermandades,
  allBandas,
  isLoading,
  onOpenChange,
}: {
  days: DiaSS[];
  config: ProgramaSSConfig | null;
  cityMapUrl: string | null;
  allHermandades: JartinetHermandad[];
  allBandas: Banda[];
  isLoading?: boolean;
  /** Avisa de si el libro está abierto (la portada oculta su cabecera). */
  onOpenChange?: (open: boolean) => void;
}) {
  const [isOpen, setIsOpenState] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState(1);
  const [sonido, setSonido] = useState(true);

  const setIsOpen = (open: boolean) => {
    setIsOpenState(open);
    onOpenChange?.(open);
  };

  const isLoadingData = !!isLoading;

  // La carilla de la web mide exactamente lo mismo que la del render (600×800,
  // `CarillaFija` solo la escala), así que el cuadro de horarios se aprieta
  // también aquí: se ve igual que en el librito del mod y no hace falta
  // scrollear la tabla para leer el itinerario entero.
  const pages = useMemo(
    () => buildPages(days, cityMapUrl, { ancho: CARILLA_ANCHO, alto: CARILLA_ALTO }),
    [days, cityMapUrl],
  );
  const spreads = useMemo(() => buildSpreads(pages), [pages]);

  // Escala del mapa (bloques de Minecraft por punto porcentual) y metros que
  // mide la Carrera Oficial: con eso se puede dar en kilómetros lo que anda
  // cada cofradía. Si el mapa no está calibrado, simplemente no se enseña.
  const escala = useMemo(() => escalaDelMapa(config?.mapCalib), [config?.mapCalib]);
  const metrosCarreraOficial = useMemo(
    () => longitudEnMetros(config?.carreraOficial, escala),
    [config?.carreraOficial, escala],
  );

  const goTo = (index: number, dir: number) => {
    if (index < 0 || index >= spreads.length) return;
    setDirection(dir);
    setCurrentIndex(index);
    if (sonido) playPageTurn();
  };

  const jumpToDay = (dia: DiaSS) => {
    const idx = spreads.findIndex((s) => s.left.dia.id === dia.id || s.right?.dia.id === dia.id);
    if (idx !== -1) goTo(idx, idx > currentIndex ? 1 : -1);
  };

  if (isLoadingData) {
    return (
      <div className="flex min-h-[24rem] items-center justify-center">
        <Loader2 className="h-12 w-12 animate-spin text-primary" />
      </div>
    );
  }

  if (spreads.length === 0) {
    return (
      <div className="rounded-lg border-2 border-dashed py-16 text-center">
        <Shield className="mx-auto h-12 w-12 text-muted-foreground" />
        <h3 className="mt-4 text-lg font-semibold">Programa no disponible</h3>
        <p className="mt-1 text-sm text-muted-foreground">El programa oficial de la Semana Santa aún no ha sido publicado.</p>
      </div>
    );
  }

  // Si el programa cambia (menos días), el índice guardado puede quedarse fuera.
  const safeIndex = Math.min(currentIndex, spreads.length - 1);
  const currentSpread = spreads[safeIndex];
  const currentDia = currentSpread.left.dia;

  return (
    <div className="flex flex-col gap-4">
      <AnimatePresence mode="wait">
        {!isOpen ? (
          <motion.div
            key="closed-book"
            initial={{ opacity: 0, scale: 0.92 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.92 }}
            transition={{ duration: 0.4 }}
            className="flex flex-col items-center gap-6 py-8"
          >
            <motion.button
              onClick={() => { setIsOpen(true); if (sonido) playPageTurn(); }}
              whileHover={{ scale: 1.02, rotateY: -4 }}
              whileTap={{ scale: 0.98 }}
              style={{ perspective: 1200 }}
              className="group relative aspect-[3/4] w-full max-w-md overflow-hidden rounded-2xl border border-white/10 shadow-[0_20px_60px_rgba(0,0,0,0.5)]"
            >
              {config?.portadaLibroUrl ? (
                // La portada se ve ENTERA (`object-contain`); detrás va la misma
                // imagen ampliada y desenfocada para que no queden franjas vacías.
                <>
                  <img src={config.portadaLibroUrl} alt="" aria-hidden className="absolute inset-0 h-full w-full scale-110 object-cover blur-2xl opacity-60" />
                  <img src={config.portadaLibroUrl} alt="Portada del programa" className="relative h-full w-full object-contain" />
                </>
              ) : (
                <div className="flex h-full w-full flex-col items-center justify-center gap-4 bg-gradient-to-br from-primary/20 via-card to-background">
                  <BookOpen className="h-16 w-16 text-primary" />
                  <span className="font-headline text-2xl font-bold text-primary">Semana Santa 2026</span>
                </div>
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-transparent to-transparent opacity-0 transition-opacity group-hover:opacity-100" />
              <div className="absolute inset-x-0 bottom-0 flex items-center justify-center gap-2 p-4 text-sm font-semibold text-white opacity-0 backdrop-blur-sm transition-opacity group-hover:opacity-100">
                <BookOpen className="h-4 w-4" /> Abrir el libro
              </div>
            </motion.button>
            <p className="text-sm text-muted-foreground">Haz clic en la portada para abrir el libro</p>
          </motion.div>
        ) : (
          <motion.div key="open-book" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }} className="flex flex-col gap-3">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex flex-wrap gap-1.5">
                {days.map((dia) => (
                  <button
                    key={dia.id}
                    onClick={() => jumpToDay(dia)}
                    className={cn(
                      'rounded-full border px-3 py-1 text-xs font-medium transition-colors',
                      currentDia?.id === dia.id ? 'border-primary/50 bg-primary/15 text-primary' : 'border-white/10 text-muted-foreground hover:border-white/20 hover:text-foreground'
                    )}
                  >
                    {dia.nombredia}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => setSonido((v) => !v)}
                  title={sonido ? 'Silenciar el pasar de página' : 'Activar el sonido de página'}
                >
                  {sonido ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4 text-muted-foreground" />}
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setIsOpen(false)}>
                  <X className="h-4 w-4" /> Cerrar libro
                </Button>
              </div>
            </div>

            <div className="flex items-center justify-center gap-2 md:gap-4">
              <Button variant="outline" size="icon" className="h-14 w-10 flex-shrink-0 rounded-full md:h-16 md:w-12" onClick={() => goTo(safeIndex - 1, -1)} disabled={safeIndex === 0}>
                <ChevronLeft className="h-5 w-5" />
              </Button>

              {/* El pliego no se anima entero: se voltea una sola carilla,
                  como una hoja de un libro de verdad. */}
              {/* Sin `min-h-[80vh]`: el alto lo pone la proporción de la carilla
                  (`CarillaFija`), que es fija. Así el pliego se hace más grande
                  o más pequeño, pero la maqueta de dentro no se mueve. */}
              <div className="relative grid w-full grid-cols-1 gap-px overflow-hidden rounded-2xl border border-white/10 bg-card shadow-2xl md:grid-cols-2">
                {/* Sombra del lomo, entre las dos carillas */}
                <div className="pointer-events-none absolute inset-y-0 left-1/2 z-20 hidden w-8 -translate-x-1/2 bg-gradient-to-r from-transparent via-black/35 to-transparent md:block" />

                <div className="border-b border-white/5 p-1 sm:p-2 md:border-b-0 md:border-r md:p-3">
                  <BookLeaf side="left" pageKey={`l-${safeIndex}`} direction={direction}>
                    <CarillaFija>
                      <HalfPageSlot page={currentSpread.left} allHermandades={allHermandades} allBandas={allBandas} cityMapUrl={cityMapUrl} carreraOficial={config?.carreraOficial || []} catedralPunto={config?.catedralPunto} escala={escala} metrosCarreraOficial={metrosCarreraOficial} />
                    </CarillaFija>
                  </BookLeaf>
                </div>
                <div className="p-1 sm:p-2 md:p-3">
                  <BookLeaf side="right" pageKey={`r-${safeIndex}`} direction={direction}>
                    <CarillaFija>
                      <HalfPageSlot page={currentSpread.right} allHermandades={allHermandades} allBandas={allBandas} cityMapUrl={cityMapUrl} carreraOficial={config?.carreraOficial || []} catedralPunto={config?.catedralPunto} escala={escala} metrosCarreraOficial={metrosCarreraOficial} />
                    </CarillaFija>
                  </BookLeaf>
                </div>
              </div>

              <Button variant="outline" size="icon" className="h-14 w-10 flex-shrink-0 rounded-full md:h-16 md:w-12" onClick={() => goTo(safeIndex + 1, 1)} disabled={safeIndex === spreads.length - 1}>
                <ChevronRight className="h-5 w-5" />
              </Button>
            </div>

            <p className="text-center text-xs text-muted-foreground">
              {currentSpread.right
                ? `Carillas ${safeIndex * 2 + 1}–${safeIndex * 2 + 2} de ${pages.length}`
                : `Carilla ${safeIndex * 2 + 1} de ${pages.length}`}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
